/**
 * GPU-resident debris tracers (phase 4). Same algorithm as tracers.ts, the
 * CPU reference: midpoint RK2 through the composed wind field, respawn in
 * an annulus around the storm center when a tracer leaves the domain,
 * exceeds max age, or goes below ground.
 *
 * Storage: two vec4 buffers, pos = (x, y, z, age) and prev = (x, y, z, 0).
 * The render material reads both and interpolates by alpha, so positions
 * never return to the CPU during normal running.
 *
 * Kernels assign every output on every invocation (via select, never a
 * conditional write). On the WebGL2 backend three.js runs compute through
 * transform feedback, which writes every element, so a skipped write would
 * be undefined there.
 *
 * Differences from the CPU reference, by design:
 * - float32 arithmetic throughout;
 * - respawn randomness comes from a hash of (index, step counter), not the
 *   CPU's sequential mulberry32 stream. Respawned tracers therefore differ;
 *   the validation excludes them and reports how many there were.
 */
import {
  Vector2,
  type Node,
  type StorageBufferNode,
  type StorageInstancedBufferAttribute,
} from 'three/webgpu';
import type { WebGPURenderer } from 'three/webgpu';
import {
  Fn,
  cos,
  float,
  hash,
  instanceIndex,
  instancedArray,
  select,
  sin,
  sqrt,
  uint,
  uniform,
  vec3,
  vec4,
} from 'three/tsl';
import type { TracerOptions, Tracers } from '../tracers';
import type { WindField } from '../windField';
import { createWindTsl } from './windFieldTsl';

export interface GpuTracers {
  readonly capacity: number;
  /** Active tracers occupy indices [0, count). */
  readonly count: number;
  /** Storage nodes, for the render material. */
  readonly posNode: StorageBufferNode<'vec4'>;
  readonly prevNode: StorageBufferNode<'vec4'>;
  /** Copy the first `cpu.count` CPU tracers to the GPU and set count. Not hot path. */
  uploadFrom(cpu: Tracers): void;
  /** Change the active count; newly active tracers are spawned on the GPU. */
  setCount(n: number): void;
  /** Respawn every active tracer (e.g. after Rmax changes). */
  respawnAll(): void;
  /** One RK2 step for all active tracers. Allocation-free. */
  step(dtS: number, tS: number): void;
  /** Shift all positions by (-dx, -dz). Used by the world rebase. */
  shift(dxM: number, dzM: number): void;
  /** Re-read wind parameters (call after any wind slider change). */
  syncWind(): void;
  /** Read positions back: [x, y, z, age] per tracer. Slow; validation only. */
  readPositions(): Promise<Float32Array>;
  dispose(): void;
}

const TWO_PI = 2 * Math.PI;

export function createGpuTracers(
  renderer: WebGPURenderer,
  field: WindField,
  options: TracerOptions,
  capacity: number,
): GpuTracers {
  const posNode = instancedArray(capacity, 'vec4');
  const prevNode = instancedArray(capacity, 'vec4');
  const wind = createWindTsl(field);
  const stageA = wind.createStage(); // t
  const stageB = wind.createStage(); // t + dt/2
  const uCenterC = uniform(new Vector2()); // t + dt, for domain test and respawn

  const uDt = uniform(0);
  const uMaxAge = uniform(0);
  const uDomainR2 = uniform(0);
  const uSpawnR0 = uniform(0);
  const uSpawnR1 = uniform(0);
  const uSpawnY0 = uniform(0);
  const uSpawnY1 = uniform(0);
  const uSalt = uniform(0, 'uint');
  const uRangeStart = uniform(0, 'uint');
  const uShift = uniform(new Vector2());
  let stepCounter = 0;
  let count = 0;

  function syncOptions(): void {
    // Infinity does not survive as a uniform on every backend; use a huge finite value.
    uMaxAge.value = Number.isFinite(options.maxAgeS) ? options.maxAgeS : 3e38;
    const dr = Number.isFinite(options.domainRadiusM) ? options.domainRadiusM : 1.8e19;
    uDomainR2.value = dr * dr;
    uSpawnR0.value = options.spawnInnerM;
    uSpawnR1.value = options.spawnOuterM;
    uSpawnY0.value = options.spawnMinHeightM;
    uSpawnY1.value = options.spawnMaxHeightM;
  }

  /** Spawn position (xyz) and age around center C, from hashed randoms. */
  function spawnNode(staggerAge: boolean): Node<'vec4'> {
    const seed = instanceIndex.toUint().mul(uint(4)).add(uSalt);
    const h0 = hash(seed);
    const h1 = hash(seed.add(uint(1)));
    const h2 = hash(seed.add(uint(2)));
    const h3 = hash(seed.add(uint(3)));
    const r02 = uSpawnR0.mul(uSpawnR0);
    const r12 = uSpawnR1.mul(uSpawnR1);
    const r = sqrt(r02.add(r12.sub(r02).mul(h0)));
    const th = h1.mul(TWO_PI);
    const y = uSpawnY0.add(uSpawnY1.sub(uSpawnY0).mul(h2));
    const age = staggerAge ? h3.mul(uMaxAge) : float(0);
    return vec4(uCenterC.x.add(r.mul(cos(th))), y, uCenterC.y.add(r.mul(sin(th))), age);
  }

  const stepKernel = Fn(() => {
    const pos = posNode.element(instanceIndex);
    const prev = prevNode.element(instanceIndex);
    const p = pos.xyz.toVar();
    const halfDt = uDt.mul(0.5);
    const k1 = wind.sample(p, stageA);
    const k2 = wind.sample(p.add(k1.mul(halfDt)), stageB);
    const np = p.add(k2.mul(uDt));
    const age = pos.w.add(uDt);
    const ox = np.x.sub(uCenterC.x);
    const oz = np.z.sub(uCenterC.y);
    const dead = age
      .greaterThan(uMaxAge)
      .or(ox.mul(ox).add(oz.mul(oz)).greaterThan(uDomainR2))
      .or(np.y.lessThan(0));
    const spawned = spawnNode(false);
    const moved = vec4(np, age);
    const next = select(dead, spawned, moved);
    prev.assign(vec4(select(dead, spawned.xyz, p), 0));
    pos.assign(next);
  })().compute(capacity);

  const spawnKernel = Fn(() => {
    const pos = posNode.element(instanceIndex);
    const prev = prevNode.element(instanceIndex);
    const doIt = instanceIndex.toUint().greaterThanEqual(uRangeStart);
    const spawned = spawnNode(true);
    const keepPos = pos.toVar();
    const keepPrev = prev.toVar();
    pos.assign(select(doIt, spawned, keepPos));
    prev.assign(select(doIt, vec4(spawned.xyz, 0), keepPrev));
  })().compute(capacity);

  const shiftKernel = Fn(() => {
    const pos = posNode.element(instanceIndex);
    const prev = prevNode.element(instanceIndex);
    const d = vec3(uShift.x, 0, uShift.y);
    pos.assign(vec4(pos.xyz.sub(d), pos.w));
    prev.assign(vec4(prev.xyz.sub(d), 0));
  })().compute(capacity);

  function nextSalt(): void {
    stepCounter = (stepCounter + 1) >>> 0;
    // Spread consecutive steps far apart in hash input space.
    uSalt.value = Math.imul(stepCounter, 0x9e3779b1) >>> 0;
  }

  function setCenterC(tS: number): void {
    const tr = field.translation;
    uCenterC.value.set(tr.originXM + tr.steerXMps * tS, tr.originZM + tr.steerZMps * tS);
  }

  /** Spawn indices [start, end) at time tS. */
  function spawnRange(start: number, end: number, tS: number): void {
    if (end <= start) return;
    syncOptions();
    setCenterC(tS);
    nextSalt();
    uRangeStart.value = start;
    void renderer.compute(spawnKernel, end);
  }

  let lastT = 0;

  return {
    capacity,
    get count(): number {
      return count;
    },
    posNode,
    prevNode,
    uploadFrom(cpu: Tracers): void {
      const n = Math.min(cpu.count, capacity);
      const pa = (posNode.value as StorageInstancedBufferAttribute).array as Float32Array;
      const qa = (prevNode.value as StorageInstancedBufferAttribute).array as Float32Array;
      for (let i = 0; i < n; i++) {
        const o = i * 4;
        pa[o] = cpu.posX[i] as number;
        pa[o + 1] = cpu.posY[i] as number;
        pa[o + 2] = cpu.posZ[i] as number;
        pa[o + 3] = cpu.age[i] as number;
        qa[o] = cpu.prevX[i] as number;
        qa[o + 1] = cpu.prevY[i] as number;
        qa[o + 2] = cpu.prevZ[i] as number;
        qa[o + 3] = 0;
      }
      (posNode.value as StorageInstancedBufferAttribute).needsUpdate = true;
      (prevNode.value as StorageInstancedBufferAttribute).needsUpdate = true;
      count = n;
    },
    setCount(n: number): void {
      const next = Math.max(0, Math.min(n, capacity));
      if (next > count) spawnRange(count, next, lastT);
      count = next;
    },
    respawnAll(): void {
      spawnRange(0, count, lastT);
    },
    step(dtS: number, tS: number): void {
      if (count === 0) return;
      syncOptions();
      wind.syncParams();
      stageA.set(tS);
      stageB.set(tS + 0.5 * dtS);
      setCenterC(tS + dtS);
      uDt.value = dtS;
      nextSalt();
      void renderer.compute(stepKernel, count);
      lastT = tS + dtS;
    },
    shift(dxM: number, dzM: number): void {
      if (count === 0) return;
      uShift.value.set(dxM, dzM);
      void renderer.compute(shiftKernel, count);
    },
    syncWind(): void {
      wind.syncParams();
    },
    async readPositions(): Promise<Float32Array> {
      const buf = await renderer.getArrayBufferAsync(posNode.value);
      return new Float32Array(buf);
    },
    dispose(): void {
      (posNode.value as StorageInstancedBufferAttribute).array = new Float32Array(0);
      (prevNode.value as StorageInstancedBufferAttribute).array = new Float32Array(0);
    },
  };
}
